import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ArrowLeft, Building2, MapPin, Globe, Star, Users as UsersIcon, Briefcase, Wallet, CreditCard, MessageSquare, Zap, Check, X, ExternalLink } from "lucide-react";

const STATUS_CLS = {
  approved: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  rejected: "bg-red-100 text-red-700",
  active: "bg-emerald-100 text-emerald-700",
};

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-xl border border-black/5 p-4">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <p className="font-heading font-black tracking-tighter text-2xl text-slate-900 mt-1">{value ?? "—"}</p>
    </div>
  );
}

export function AdminCompanyProfileSection({ companyId, onBack, onOpenJob }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/companies/${companyId}`);
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const moderate = async (action) => {
    setSaving(true);
    try {
      await api.patch(`/admin/companies/${companyId}`, { action });
      toast.success(action === "approve" ? "Empresa aprovada." : "Empresa rejeitada.");
      load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };

  if (!data) return <div className="text-slate-500 text-sm">A carregar...</div>;
  const { company, owner, jobs, reviews, transactions, boosts, stats } = data;

  return (
    <div className="space-y-6">
      <button type="button" data-testid="admin-company-profile-back" onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Voltar às empresas
      </button>

      <div className="bg-white rounded-xl border border-black/5 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 data-testid="admin-company-profile-name" className="font-heading font-black tracking-tighter text-2xl text-slate-900 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-slate-400" />{company.company_name}
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Badge className={`${STATUS_CLS[company.status] || "bg-slate-100 text-slate-700"} hover:${STATUS_CLS[company.status]}`}>{company.status}</Badge>
              {company._demo && <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50">demo</Badge>}
              {company.district && <span className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{company.district}</span>}
              {company.avg_rating && <span className="text-xs text-slate-500 flex items-center gap-1"><Star className="w-3 h-3 fill-amber-500 text-amber-500" />{company.avg_rating.toFixed(1)}</span>}
              {company.website && <a href={company.website} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-amber-600 flex items-center gap-1"><Globe className="w-3 h-3" />{company.website}</a>}
              {company.slug && <a href={`/empresas/${company.slug}`} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-amber-600 flex items-center gap-1"><ExternalLink className="w-3 h-3" />p\u00e1gina p\u00fablica</a>}
            </div>
            {company.about && <p className="text-sm text-slate-600 mt-3 max-w-2xl">{company.about}</p>}
          </div>
          {company.status === "pending" && (
            <div className="flex gap-2">
              <Button data-testid="admin-company-approve" size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" disabled={saving} onClick={() => moderate("approve")}>
                <Check className="w-4 h-4 mr-1.5" />Aprovar
              </Button>
              <Button data-testid="admin-company-reject" size="sm" variant="destructive" className="rounded-full" disabled={saving} onClick={() => moderate("reject")}>
                <X className="w-4 h-4 mr-1.5" />Rejeitar
              </Button>
            </div>
          )}
        </div>
        {owner && (
          <div className="mt-4 pt-4 border-t border-black/5 text-sm text-slate-600">
            <span className="text-slate-500">Titular da conta:</span> <b>{owner.name || "—"}</b> · <span className="text-slate-500">{owner.email}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Briefcase} label="Vagas totais" value={stats.total_jobs} />
        <Stat icon={Briefcase} label="Vagas ativas" value={stats.active_jobs} />
        <Stat icon={MessageSquare} label="Candidaturas" value={stats.total_applications} />
        <Stat icon={Star} label="Reviews" value={stats.total_reviews} />
        <Stat icon={Wallet} label="Wallet SILVER" value={(stats.wallet?.silver || 0) + (stats.wallet?.destaque || 0)} />
        <Stat icon={Wallet} label="Wallet GOLD" value={(stats.wallet?.gold || 0) + (stats.wallet?.destaque_max || 0)} />
        <Stat icon={Wallet} label="Wallet EXPRESS" value={stats.wallet?.express || 0} />
        <Stat icon={Wallet} label="Análises IA" value={stats.wallet?.ai_screening || 0} />
        <Stat icon={CreditCard} label="Total pago" value={`${(stats.total_paid_cents / 100).toFixed(0)}\u20ac`} />
        <Stat icon={Zap} label="Boosts hist\u00f3ricos" value={boosts?.length || 0} />
      </div>

      {jobs.length > 0 && (
        <div className="bg-white rounded-xl border border-black/5 overflow-hidden">
          <div className="px-5 py-3 border-b border-black/5">
            <p className="font-heading font-bold tracking-tighter text-slate-900">Vagas desta empresa</p>
          </div>
          <ul className="divide-y divide-black/5">
            {jobs.map((j) => (
              <li key={j.id}>
                <button type="button" data-testid={`admin-company-job-${j.id}`} onClick={() => onOpenJob(j.id)}
                  className="w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-50 text-left transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{j.title}</p>
                    <p className="text-xs text-slate-500">{j.concelho}, {j.district} · {j.regime} · {j.work_mode}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`${STATUS_CLS[j.status] || "bg-slate-100 text-slate-700"} hover:${STATUS_CLS[j.status]}`}>{j.status}</Badge>
                    {j.boost?.tier && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">{j.boost.tier}</Badge>}
                    <span className="text-xs text-slate-500">{j.applications_count || 0} cand.</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {reviews.length > 0 && (
        <div className="bg-white rounded-xl border border-black/5 p-5">
          <p className="font-heading font-bold tracking-tighter text-slate-900 mb-3">Reviews ({reviews.length})</p>
          <ul className="space-y-3">
            {reviews.slice(0, 10).map((r) => (
              <li key={r.id} className="border-b border-black/5 pb-3 last:border-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="flex items-center gap-0.5">{Array.from({ length: 5 }).map((_, i) => (
                    <Star key={`star-${r.id}-${i}`} className={`w-3 h-3 ${i < (r.rating || 0) ? "fill-amber-500 text-amber-500" : "text-slate-200"}`} />
                  ))}</span>
                  <span className="text-xs text-slate-500">{r.candidate_name || "An\u00f3nimo"} · {r.created_at ? new Date(r.created_at).toLocaleDateString("pt-PT") : ""}</span>
                </div>
                <p className="text-sm text-slate-700">{r.comment}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {transactions.length > 0 && (
        <div className="bg-white rounded-xl border border-black/5 p-5">
          <p className="font-heading font-bold tracking-tighter text-slate-900 mb-3">Pagamentos ({transactions.length})</p>
          <ul className="space-y-2">
            {transactions.slice(0, 20).map((t) => (
              <li key={t.id} className="flex flex-wrap items-center justify-between text-sm border-b border-black/5 pb-2 last:border-0">
                <span className="text-slate-700">{t.label || t.tier || t.pack || "Pagamento"} · <span className="text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString("pt-PT") : ""}</span></span>
                <span className="flex items-center gap-2">
                  <span className="tabular-nums">{((t.amount || 0) / 100).toFixed(2)}\u20ac</span>
                  <Badge className={t.payment_status === "paid" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-600 hover:bg-slate-100"}>{t.payment_status || t.status}</Badge>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
